import type { MessageChannelV3 } from "@redotech/redo-flows-common/rules-v3/types";
import type { StopTopicContinuationAction as StopTopicContinuationActionType } from "@redotech/redo-flows-common/rules/schemas";
import { useTranslation } from "@redotech/redo-i18n";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { channelDisplayNames, channelItems } from "../channel-v3-items";

export const StopTopicContinuationAction = memo(
  function StopTopicContinuationAction({
    action,
    onChange,
    editableFields,
  }: {
    action: StopTopicContinuationActionType;
    onChange: (updated: StopTopicContinuationActionType) => void;
    editableFields?: ReadonlySet<string>;
  }) {
    const { t } = useTranslation();
    const channelsEditable =
      editableFields === undefined || editableFields.has("channels");

    const [dropdownButtonRef, setDropdownButtonRef] =
      useState<HTMLButtonElement | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const selectedItems: RedoListItem<MessageChannelV3>[] = useMemo(() => {
      return (action.channels ?? []).map((channel) => ({
        id: channel,
        type: "text" as const,
        text: channelDisplayNames()[channel],
        value: channel,
      }));
    }, [action.channels]);

    const displayItem: RedoListItem<MessageChannelV3> | undefined =
      useMemo(() => {
        if (selectedItems.length === 0) {
          return undefined;
        }
        if (selectedItems.length === 1) {
          return selectedItems[0];
        }
        return {
          id: "display",
          type: "text" as const,
          text: selectedItems
            .flatMap((item) => ("text" in item ? [item.text] : []))
            .join(", "),
          value: "gmail" as MessageChannelV3,
        };
      }, [selectedItems]);

    return (
      <Flex dir="column" gap="sm" w="full">
        <Text fontSize="xs" textColor="secondary">
          {t("Stop continuation for channels")}
        </Text>
        <RedoSingleSelectDropdownInputButton
          disabled={!channelsEditable}
          isDropdownOpen={dropdownOpen}
          onClick={() => channelsEditable && setDropdownOpen(!dropdownOpen)}
          placeholder={t("Select channels")}
          ref={setDropdownButtonRef}
          selectedItem={displayItem}
          size="xs"
        />
        <RedoMultiselectDropdown
          dropdownAnchor={dropdownButtonRef}
          dropdownOpen={dropdownOpen}
          fitToAnchor
          items={channelItems()}
          selectedItems={selectedItems}
          selectionVariant="checkbox"
          setDropdownOpen={setDropdownOpen}
          setSelectedItems={(items) => {
            if (!channelsEditable) {
              return;
            }
            onChange({ ...action, channels: items.map((item) => item.value) });
          }}
          size="xs"
        />
      </Flex>
    );
  },
);
